import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../../components/OnboardingLayout'
import OnboardingField from '../../components/onboarding/OnboardingField'
import { invalidFieldsFromChecks, scrollToFirstFieldError } from '../../lib/onboarding/fieldValidation'
import { ONBOARDING, onboardingChipClass, onboardingFieldControlClass } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'
import type { OnboardingStepProps } from './stepProps'

const SUPPLEMENT_OPTIONS = [
  { value: 'Pre-workout', key: 'preWorkout' },
  { value: 'Creatine', key: 'creatine' },
  { value: 'Protein powder', key: 'proteinPowder' },
  { value: 'Multivitamin', key: 'multivitamin' },
  { value: 'Fish oil', key: 'fishOil' },
  { value: 'Magnesium', key: 'magnesium' },
  { value: 'Vitamin D', key: 'vitaminD' },
] as const

const KITCHEN_EQUIPMENT_OPTIONS = [
  { value: 'Blender', key: 'blender' },
  { value: 'Slow cooker', key: 'slowCooker' },
  { value: 'Air fryer', key: 'airFryer' },
  { value: 'Rice cooker', key: 'riceCooker' },
  { value: 'Full kitchen', key: 'fullKitchen' },
] as const

const COOK_TIME_PRESETS = [
  { value: '15', key: 'time15' },
  { value: '30', key: 'time30' },
  { value: '45', key: 'time45' },
  { value: '60', key: 'time60' },
] as const

const Step5 = ({ onNext, onPrevious, initialData, stepIndex }: OnboardingStepProps) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    allergies: (initialData?.allergies as string) || '',
    food_preferences: (initialData?.food_preferences as string) || '',
    vegetarian: (initialData?.vegetarian as string) || 'false',
    meal_preference: (initialData?.meal_preference as string) || '',
    cooking_skill_level: (initialData?.cooking_skill_level as string) || '',
    cook_time_available: (initialData?.cook_time_available as string) || '',
    kitchen_equipment: (initialData?.kitchen_equipment as string[]) || [],
    supplement_stack: (initialData?.supplement_stack as string[]) || [],
    meal_frequency: (initialData?.meal_frequency as string) || '',
    eating_schedule_type: (initialData?.eating_schedule_type as string) || '',
    fasting_preference: (initialData?.fasting_preference as string) || '',
    fasting_window: (initialData?.fasting_window as string) || '',
  })
  const [error, setError] = useState('')
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set())

  const handleSubmit = () => {
    setError('')

    const checks = {
      meal_preference: Boolean(formData.meal_preference),
      meal_frequency: Boolean(formData.meal_frequency),
      cooking_skill_level: Boolean(formData.cooking_skill_level),
      cook_time_available: Boolean(formData.cook_time_available.trim()),
    }
    const invalid = invalidFieldsFromChecks(checks)
    if (invalid.size > 0) {
      setInvalidFields(invalid)
      setError(t('onboarding.nutrition.fillRequired'))
      scrollToFirstFieldError([...invalid])
      return
    }

    setInvalidFields(new Set())
    const output: Record<string, unknown> = {
      allergies: formData.allergies,
      food_preferences: formData.food_preferences,
      vegetarian: formData.vegetarian === 'true',
      meal_preference: formData.meal_preference,
      cooking_skill_level: formData.cooking_skill_level,
      cook_time_available: formData.cook_time_available,
      kitchen_equipment: formData.kitchen_equipment,
      supplement_stack: formData.supplement_stack,
      meal_frequency: formData.meal_frequency,
      eating_schedule_type: formData.eating_schedule_type,
      fasting_preference: formData.fasting_preference,
    }

    if (formData.fasting_preference === 'intermittent' && formData.fasting_window.trim()) {
      output.fasting_window = formData.fasting_window
    }

    onNext(output)
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setInvalidFields((prev) => {
      if (!prev.has(name)) return prev
      const next = new Set(prev)
      next.delete(name)
      return next
    })
  }

  const toggle = (field: 'kitchen_equipment' | 'supplement_stack', item: string) => {
    const list = formData[field]
    setFormData({
      ...formData,
      [field]: list.includes(item) ? list.filter((x) => x !== item) : [...list, item],
    })
  }

  const selectCookTime = (minutes: string) => {
    setFormData({ ...formData, cook_time_available: minutes })
    setInvalidFields((prev) => {
      if (!prev.has('cook_time_available')) return prev
      const next = new Set(prev)
      next.delete('cook_time_available')
      return next
    })
  }

  return (
    <OnboardingLayout step={stepIndex} totalSteps={ONBOARDING_STEP_COUNT} onNext={handleSubmit} onPrev={onPrevious}>
      <form className="space-y-4">
        <div>
          <h2 className={ONBOARDING.subheading}>{t('onboarding.nutrition.heading')}</h2>
          <p className={ONBOARDING.subtitle}>{t('onboarding.nutrition.subtitle')}</p>
        </div>

        <label className="block space-y-2">
          <span className={ONBOARDING.label}>{t('onboarding.nutrition.allergies')}</span>
          <textarea name="allergies" value={formData.allergies} onChange={handleChange} rows={2} className={ONBOARDING.textarea} />
        </label>

        <label className="block space-y-2">
          <span className={ONBOARDING.label}>{t('onboarding.nutrition.dislikes')}</span>
          <textarea name="food_preferences" value={formData.food_preferences} onChange={handleChange} rows={2} className={ONBOARDING.textarea} />
        </label>

        <OnboardingField id="meal_preference" label={t('onboarding.nutrition.mealPref')} required invalid={invalidFields.has('meal_preference')}>
          <select
            name="meal_preference"
            value={formData.meal_preference}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('meal_preference'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="repeat">{t('onboarding.nutrition.mealPrefRepeat')}</option>
            <option value="vary">{t('onboarding.nutrition.mealPrefVary')}</option>
            <option value="mixed">{t('onboarding.nutrition.mealPrefMixed')}</option>
          </select>
        </OnboardingField>

        <div className="space-y-2">
          <span className={ONBOARDING.label}>{t('onboarding.nutrition.supplements')}</span>
          <div className="grid grid-cols-2 gap-2">
            {SUPPLEMENT_OPTIONS.map(({ value, key }) => (
              <button
                key={value}
                type="button"
                onClick={() => toggle('supplement_stack', value)}
                className={onboardingChipClass(formData.supplement_stack.includes(value))}
              >
                {t(`onboarding.nutrition.supps.${key}`)}
              </button>
            ))}
          </div>
        </div>

        <OnboardingField id="meal_frequency" label={t('onboarding.nutrition.mealFreq')} required invalid={invalidFields.has('meal_frequency')}>
          <select
            name="meal_frequency"
            value={formData.meal_frequency}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('meal_frequency'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="3_meals">{t('onboarding.nutrition.freq3')}</option>
            <option value="4_meals">{t('onboarding.nutrition.freq4')}</option>
            <option value="5_plus_meals">{t('onboarding.nutrition.freq6')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="cooking_skill_level" label={t('onboarding.nutrition.cookingSkill')} required invalid={invalidFields.has('cooking_skill_level')}>
          <select
            name="cooking_skill_level"
            value={formData.cooking_skill_level}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('cooking_skill_level'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="beginner">{t('onboarding.nutrition.skillBeginner')}</option>
            <option value="intermediate">{t('onboarding.nutrition.skillIntermediate')}</option>
            <option value="advanced">{t('onboarding.nutrition.skillAdvanced')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="cook_time_available" label={t('onboarding.nutrition.cookTime')} required invalid={invalidFields.has('cook_time_available')}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {COOK_TIME_PRESETS.map(({ value, key }) => (
              <button
                key={value}
                type="button"
                onClick={() => selectCookTime(value)}
                className={onboardingChipClass(formData.cook_time_available === value)}
              >
                {t(`onboarding.nutrition.${key}`)}
              </button>
            ))}
          </div>
          <label className="block space-y-2">
            <span className={`text-sm ${ONBOARDING.muted}`}>{t('onboarding.nutrition.cookTimeCustom')}</span>
            <input
              name="cook_time_available"
              value={formData.cook_time_available}
              onChange={handleChange}
              inputMode="numeric"
              placeholder={t('onboarding.nutrition.cookTimePlaceholder')}
              className={onboardingFieldControlClass(ONBOARDING.input, invalidFields.has('cook_time_available'))}
            />
          </label>
        </OnboardingField>

        <div className="space-y-2">
          <span className={ONBOARDING.label}>{t('onboarding.nutrition.kitchenEquip')}</span>
          <div className="grid grid-cols-2 gap-2">
            {KITCHEN_EQUIPMENT_OPTIONS.map(({ value, key }) => (
              <button
                key={value}
                type="button"
                onClick={() => toggle('kitchen_equipment', value)}
                className={onboardingChipClass(formData.kitchen_equipment.includes(value))}
              >
                {t(`onboarding.nutrition.equip.${key}`)}
              </button>
            ))}
          </div>
        </div>

        {error ? <div className={ONBOARDING.error}>{error}</div> : null}
      </form>
    </OnboardingLayout>
  )
}

export default Step5
